'use client';

import * as React from 'react';
import { ArrowUpDown, Bell, LogOut, Settings, Shield, User, type LucideIcon } from 'lucide-react';
import type { CityCode } from '@loci/city-editions';

import { DialogClose } from '@/components/atoms/primitives/dialog';
import { cn } from '@/components/atoms/primitives/utils';
import {
  CityAuthAvatar,
  FOCUS_RING,
  isCrossOrigin,
} from '@/components/molecules/CityHeader/cityHeaderShared';
import { useCityReturnUrl } from '@/hooks/useCityReturnUrl';
import { useCityIsStaging } from '@/lib/city-context.client';
import type { CityAuthUser } from '@/lib/city-nav/cityAuthUser';
import { appUrl } from '@/lib/cross-host-urls';

import { activateHeaderWayfinder } from './MenuWayfinderCard';

/**
 * CityAccountSheetContent — the signed-in account drawer body on the
 * marketing-city surface (FRE-1389, DS 1.9.1 `buildAccount`), mounted by
 * `CityAccountMenu` inside the shared `NavMenuSheet` shell.
 *
 * The avatar answers "who am I"; the hamburger's sitemap answers "where can I
 * go" — so this body carries no hubs. Rows, in design order: identity head ·
 * Edit profile · Account · Settings · Notifications · — · Switch city · — ·
 * Sign out. Theme and Open Studio are app-layer only (Studio is role-gated and
 * the city identity payload carries no role).
 *
 * Account destinations live on `app.loci.city/<city>`. Notifications keeps the
 * city surface's in-place `NotificationPanel` (WEB-635) rather than navigating
 * to the app. Sign out returns to this city page (`/logout?next=`).
 *
 * The utilities below are the ≤720px takeover. In the ≥721px 240px dropdown
 * (FRE-1390), the `city-account-sheet*` hooks take the DS menu metrics from
 * `styles/overrides/chrome.css`.
 */

const ROW = cn(
  'city-account-sheet__row flex min-h-11 w-full items-center gap-3 rounded-ds-md px-3 py-2.5 text-left no-underline',
  'font-body text-small text-fg-1',
  'transition-colors duration-150 hover:bg-ds-glass-bg motion-reduce:transition-none',
  FOCUS_RING,
);

const ROW_ICON = 'size-3.5 shrink-0 text-fg-3';

const ROW_META =
  'city-account-sheet__meta ml-auto shrink-0 font-mono text-micro uppercase tracking-widest text-fg-3';

const DIVIDER = 'my-1 h-px bg-ds-border';

export interface CityAccountSheetContentProps {
  readonly cityCode: CityCode;
  /** City display name — the Switch city row's meta. */
  readonly cityName: string;
  readonly authUser: CityAuthUser;
  /** Request-host staging flag; falls back to the `CityStagingProvider` context. */
  readonly isStaging?: boolean;
  /** Server-authoritative unread count; zero omits the "N new" meta. */
  readonly unreadCount?: number;
  /** Opens NotificationPanel after the drawer closes (no stacked takeovers). */
  readonly onOpenNotifications?: () => void;
  /** Called before the panel opens so the host can skip drawer focus-return. */
  readonly onBeforeOpenNotifications?: () => void;
  /** Called before Switch city opens the header Wayfinder (same focus handoff). */
  readonly onBeforeOpenWayfinder?: () => void;
  readonly className?: string;
}

interface AccountLink {
  readonly key: 'edit-profile' | 'account' | 'settings';
  readonly label: string;
  readonly path: string;
  readonly icon: LucideIcon;
}

const ACCOUNT_LINKS: ReadonlyArray<AccountLink> = [
  { key: 'edit-profile', label: 'Edit profile', path: '/settings/profile', icon: User },
  { key: 'account', label: 'Account', path: '/settings/account', icon: Shield },
  { key: 'settings', label: 'Settings', path: '/settings', icon: Settings },
];

export function CityAccountSheetContent({
  cityCode,
  cityName,
  authUser,
  isStaging: isStagingProp,
  unreadCount = 0,
  onOpenNotifications,
  onBeforeOpenNotifications,
  onBeforeOpenWayfinder,
  className,
}: CityAccountSheetContentProps): React.ReactElement {
  const contextIsStaging = useCityIsStaging();
  const isStaging = isStagingProp ?? contextIsStaging;
  const returnUrl = useCityReturnUrl(cityCode, { isStaging });
  const signOutHref = appUrl({ city: null, path: '/logout', next: returnUrl, isStaging });

  const handleNotificationsClick = React.useCallback(() => {
    onBeforeOpenNotifications?.();
    onOpenNotifications?.();
  }, [onBeforeOpenNotifications, onOpenNotifications]);

  const handleSwitchCityClick = React.useCallback(() => {
    onBeforeOpenWayfinder?.();
    activateHeaderWayfinder();
  }, [onBeforeOpenWayfinder]);

  return (
    <div
      data-testid="city-account-sheet-content"
      className={cn('city-account-sheet flex min-h-0 flex-1 flex-col px-2 py-4 md:px-3', className)}
    >
      <div
        data-testid="city-account-identity"
        className="city-account-sheet__head mb-1 flex min-w-0 items-center gap-3 border-b border-ds-border px-3 pb-4"
      >
        <CityAuthAvatar
          authUser={authUser}
          testId="city-account-identity-avatar"
          className="city-account-sheet__avatar size-10 text-small"
        />
        <span className="min-w-0">
          <span
            data-testid="city-account-identity-name"
            className="city-account-sheet__name block truncate font-display text-body font-semibold text-fg-1"
          >
            {authUser.displayName}
          </span>
          {authUser.meta ? (
            <span
              data-testid="city-account-identity-meta"
              className="city-account-sheet__handle mt-0.5 block truncate font-mono text-micro text-fg-3"
            >
              {authUser.meta}
            </span>
          ) : null}
        </span>
      </div>

      <nav aria-label="Account">
        <ul className="flex flex-col gap-0.5">
          {ACCOUNT_LINKS.map(({ key, label, path, icon: Icon }) => {
            const href = appUrl({ city: cityCode, path, isStaging });
            return (
              <li key={key}>
                <DialogClose asChild>
                  <a
                    href={href}
                    data-testid={`city-account-link-${key}`}
                    className={ROW}
                    {...(isCrossOrigin(href) ? { target: '_blank', rel: 'noreferrer' } : {})}
                  >
                    <Icon className={ROW_ICON} aria-hidden="true" />
                    <span className="truncate">{label}</span>
                  </a>
                </DialogClose>
              </li>
            );
          })}
          <li>
            <DialogClose asChild>
              <button
                type="button"
                onClick={handleNotificationsClick}
                data-testid="city-account-notifications"
                aria-label={
                  unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'
                }
                className={ROW}
              >
                <Bell className={ROW_ICON} aria-hidden="true" />
                <span className="truncate">Notifications</span>
                {unreadCount > 0 ? (
                  <span aria-hidden="true" data-testid="city-account-unread" className={ROW_META}>
                    {unreadCount} new
                  </span>
                ) : null}
              </button>
            </DialogClose>
          </li>
        </ul>
      </nav>

      <div role="separator" className={DIVIDER} />

      <DialogClose asChild>
        <button
          type="button"
          onClick={handleSwitchCityClick}
          data-testid="city-account-switch-city"
          aria-label={`Switch city, currently ${cityName}`}
          className={ROW}
        >
          <ArrowUpDown className={ROW_ICON} aria-hidden="true" />
          <span className="truncate">Switch city</span>
          <span aria-hidden="true" className={ROW_META}>
            {cityName}
          </span>
        </button>
      </DialogClose>

      <div role="separator" className={DIVIDER} />

      {/* Same tab even though the logout host is `app.loci.city`: a new tab
          would leave this city chrome signed in until a full reload. */}
      <DialogClose asChild>
        <a href={signOutHref} data-testid="city-account-sign-out" className={ROW}>
          <LogOut className={ROW_ICON} aria-hidden="true" />
          <span>Sign out</span>
        </a>
      </DialogClose>
    </div>
  );
}
